import { useContext, useMemo } from 'react';
import { generateOverlayRectangleGeometry } from 'app/agriculture/FarmMain/MapRenderer/utils/generateOverlayRectangleGeometry';
import { CesiumGroundPrimitive } from 'app/agriculture/FarmMain/MapRenderer/components/CesiumGroundPrimitive';
import { CesiumPrimitive } from 'app/agriculture/FarmMain/MapRenderer/components/CesiumPrimitive';
import { TerrainModeContext } from 'app/agriculture/contexts/TerrainProviderContext';
import { PolygonGeometry } from 'app/agriculture/queries/types/geometry/PolygonGeometry';
import { PointGeometry } from 'app/agriculture/queries/types/geometry/PointGeometry';

export function CesiumOverlayComponent({
    geometry,
    pole,
    image,
    isSelected,
}: {
    geometry: PolygonGeometry;
    pole: PointGeometry;
    image: string;
    isSelected: boolean;
}) {
    const { terrainEnabled } = useContext(TerrainModeContext);

    const rectangleGeometry = useMemo(
        () => generateOverlayRectangleGeometry({ geometry, pole }),
        [geometry, pole],
    );

    const geometryInstance = useMemo(
        () =>
            new Cesium.GeometryInstance({
                geometry: rectangleGeometry,
            }),
        [rectangleGeometry],
    );

    const selectedAppearance = useMemo(
        () =>
            new Cesium.MaterialAppearance({
                closed: true,
                renderState: { depthTest: { enabled: false } },
                material: Cesium.Material.fromType('Image', {
                    image,
                    color: Cesium.Color.WHITE,
                }),
            }),
        [image],
    );

    const unselectedAppearance = useMemo(
        () =>
            new Cesium.MaterialAppearance({
                closed: true,
                renderState: { depthTest: { enabled: false } },
                material: Cesium.Material.fromType('Image', {
                    image,
                    color: Cesium.Color.WHITE.withAlpha(0.4),
                }),
            }),
        [image],
    );

    return terrainEnabled ? (
        <CesiumGroundPrimitive
            primitiveOptions={{
                geometryInstances: geometryInstance,
                appearance: isSelected ? selectedAppearance : unselectedAppearance,
                asynchronous: false,
                releaseGeometryInstances: false,
                allowPicking: false,
            }}
        />
    ) : (
        <CesiumPrimitive
            primitiveOptions={{
                geometryInstances: geometryInstance,
                appearance: isSelected ? selectedAppearance : unselectedAppearance,
                asynchronous: false,
                releaseGeometryInstances: false,
                allowPicking: false,
            }}
        />
    );
}
